import React from "react";
import { AlertCircle, RefreshCw, Trash2 } from "lucide-react";

export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("ErrorBoundary caught an unhandled error:", error, errorInfo);
    }

    handleReload = () => {
        window.location.reload();
    };

    handleHardReset = async () => {
        try {
            if ("caches" in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k)));
            }
            if ("serviceWorker" in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map((r) => r.unregister()));
            }
        } catch (e) {
            console.warn("Reset error:", e);
        }
        window.location.href = window.location.origin + window.location.pathname + "?reset=" + Date.now();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-[#F7F6F3] flex items-center justify-center p-4">
                    <div className="max-w-md w-full bg-white rounded-3xl border border-neutral-200 shadow-xl p-6 sm:p-8 text-center animate-scale-up">
                        <div className="h-14 w-14 rounded-2xl bg-amber-50 border border-amber-200 text-amber-600 grid place-items-center mx-auto mb-4">
                            <AlertCircle size={28} />
                        </div>
                        <h2 className="font-display text-xl sm:text-2xl font-bold text-neutral-900 tracking-tight">
                            Something went wrong
                        </h2>
                        <p className="text-xs sm:text-sm text-neutral-600 mt-2">
                            A display issue occurred while loading this view. You can reload or refresh your app cache.
                        </p>

                        <div className="mt-6 flex flex-col gap-2.5">
                            <button
                                type="button"
                                onClick={this.handleReload}
                                className="w-full py-3 px-4 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white font-semibold text-sm transition cursor-pointer flex items-center justify-center gap-2 shadow-xs active:scale-95"
                            >
                                <RefreshCw size={15} />
                                <span>Reload App</span>
                            </button>
                            <button
                                type="button"
                                onClick={this.handleHardReset}
                                className="w-full py-2.5 px-4 rounded-xl border border-neutral-300 hover:bg-neutral-50 text-neutral-700 font-medium text-xs transition cursor-pointer flex items-center justify-center gap-2 active:scale-95"
                            >
                                <Trash2 size={13} />
                                <span>Clear Cache & Reload</span>
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
